import { Formik, FormikHelpers } from "formik";
import React from "react";
import { Button, Card, Col, Container, Form, Row } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";

import AuthApi from "../../api/AuthApi";
import { appConstants } from "../../config/app-constants";
import { EToastvariant, IForgotPwdForm } from "../../config/interfaces";
import { useAppDispatch } from "../../redux/Store";
import { showToastMsg } from "../../redux/slice/ToastMsgSlice";
import { StorageService, UtilService } from "../../services";
import { ButtonSpinner } from "../placeholders";

const initialValues: IForgotPwdForm = {
  email: ""
};

const validationSchema = Yup.object({
  email: Yup.string()
    .trim()
    .email("Invalid email address")
    .matches(appConstants.EMAIL_REGEX, "Invalid mail format")
    .required("Email is required")
});

const ForgotPasswordForm: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const handleForgotPwdFormSubmit = async (
    values: IForgotPwdForm,
    { setSubmitting }: FormikHelpers<IForgotPwdForm>
  ) => {
    try {
      setSubmitting(true);

      const authApi = AuthApi.getInstance();
      const sendOtpResp = await authApi.sendOTP({
        email: values.email.toLowerCase()
      });

      await StorageService.setPreVerifyData(
        JSON.stringify({
          email: values.email.toLowerCase(),
          preVerificationToken: sendOtpResp.preVerificationToken
        })
      );

      dispatch(
        showToastMsg({
          isVisible: true,
          title: "Success",
          message: "OTP sent successfully",
          variant: EToastvariant.Success
        })
      );
      navigate("/verification", {
        state: { fromScreen: "forgot-password", showVerification: true }
      });
    } catch (error) {
      dispatch(
        showToastMsg({
          isVisible: true,
          title: "Error",
          message: UtilService.getErrorMessage(error),
          variant: EToastvariant.Danger
        })
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Container>
        <div className="auth-form-wrap">
          <Row className="align-items-center justify-content-md-center">
            <Col>
              <Card bg="white" className="auth-card">
                <Card.Body>
                  <h1 className="fw-bold text-center mb-1">Forgot Password</h1>
                  <p className="text-center text-greyDark">
                    Enter your email address to reset your password
                  </p>

                  <Formik
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={handleForgotPwdFormSubmit}
                  >
                    {({
                      handleChange,
                      handleBlur,
                      handleSubmit,
                      values,
                      errors,
                      touched,
                      isSubmitting
                    }) => (
                      <Form
                        onSubmit={(e) => {
                          e.preventDefault();
                          handleSubmit();
                        }}
                      >
                        <Form.Group controlId="email" className="form-group">
                          <Form.Label className="required-field">Email</Form.Label>
                          <Form.Control
                            type="text"
                            autoComplete="false"
                            placeholder="Please enter your email"
                            name="email"
                            value={values.email}
                            onBlur={handleBlur}
                            onChange={handleChange}
                            isInvalid={!!errors.email && !!touched.email}
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.email}
                          </Form.Control.Feedback>
                        </Form.Group>

                        <div className="btn-wrap">
                          <Button
                            variant="primary"
                            type="submit"
                            size="lg"
                            className="w-100 shadow"
                            disabled={isSubmitting}
                          >
                            {!isSubmitting ? "Next" : <ButtonSpinner />}
                          </Button>
                        </div>
                      </Form>
                    )}
                  </Formik>
                </Card.Body>
              </Card>

              <div className="auth-footer-1 justify-content-end">
                <Form.Text className="m-0 text-greyDark">Back to</Form.Text>
                <Button variant="outline-greyDark" size="sm" onClick={() => navigate(-1)}>
                  Sign In
                </Button>
              </div>
            </Col>
          </Row>
        </div>
      </Container>
    </>
  );
};

export default ForgotPasswordForm;
